import { Controller } from "@hotwired/stimulus"
import { createLogger } from "../lib/logger"
import { getI18nMessage } from "../lib/i18n_helper"
import { scrollLocked } from "../lib/scroll_lock"

// Enhanced Category Picker Controller
// Accordion-style category selection with search, swipe gestures, and recent tracking
//
// Features:
// - Accordion-style inline parent/child navigation
// - Popular + Recent categories sections
// - Search functionality with real-time filtering
// - Swipe-to-close gesture for mobile
// - Recent category tracking via API
// - Breadcrumb display: "Parent > Child"
const log = createLogger('CategoryPicker')

// Constants
const BACKDROP_SUFFIX = '-backdrop'

export default class extends Controller {
  static targets = [
    "hiddenInput",
    "selectedDisplay",
    "mainView",
    "accordionList",
    "searchInput",
    "searchResults",
    "searchWrapper",
    "medicalComplianceSection"  // Medical compliance fields container
  ]

  static values = {
    tree: String,   // Category tree JSON from server
    userId: Number,  // Current user ID for recent tracking
    medicalCategories: Array  // Categories requiring medical compliance (醫療, 美容, etc.)
  }

  connect() {
    this.categoriesTree = JSON.parse(this.treeValue)
    this.categoriesCache = this.buildCategoriesCache()
    this.currentOpenParentId = null  // Track currently expanded parent
    this.touchStartY = 0
    this.currentY = 0
    this._modal = null // Cache modal element
    this.viewportResizeHandler = this.handleViewportResize.bind(this)
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', this.viewportResizeHandler)
    }
    log.info('CategoryPicker connected', {
      categoriesCount: this.categoriesTree.length,
      userId: this.userIdValue
    })

    // Initialize medical compliance section visibility on page load
    // Fixes bug where section remains visible after selecting non-medical category
    this.initializeMedicalCompliance()
  }

  disconnect() {
    this._modal = null
    if (window.visualViewport && this.viewportResizeHandler) {
      window.visualViewport.removeEventListener('resize', this.viewportResizeHandler)
    }
  }

  // Cached modal ID getter (returns base ID without "-backdrop" suffix)
  get modal() {
    if (!this._modal) {
      const backdrop = this.element.querySelector('[id^="category-picker-modal"]')
      if (backdrop) {
        // Extract base ID by removing "-backdrop" suffix
        this._modal = backdrop.id.replace(new RegExp(`${BACKDROP_SUFFIX}$`), '')
        log.debug('Modal found', { backdropId: backdrop.id, modalId: this._modal })
      } else {
        log.warn('Modal backdrop not found in DOM', {
          selector: '[id^="category-picker-modal"]',
          parentElement: this.element.id || this.element.className
        })
      }
    }
    return this._modal
  }

  // ========================================
  // CORE PICKER LOGIC
  // ========================================

  open(event) {
    if (event) event.preventDefault()

    // Render accordion BEFORE opening modal (synchronous - ensures targets are ready)
    this.renderAccordion()

    // Trigger bottom-sheet controller via custom event
    const dialogId = this.modal  // Returns base ID (string) without "-backdrop"

    if (!dialogId) {
      log.error('Modal element not found - cannot open dialog')
      return
    }

    try {
      const openEvent = new CustomEvent('dialog:open', {
        detail: { dialogId: dialogId },
        bubbles: true
      })
      window.dispatchEvent(openEvent)
      log.debug('Modal opened', { dialogId })
    } catch (error) {
      log.error('Failed to open modal', { error: error.message })
    }
  }

  close() {
    this.resetView()

    const dialogId = this.modal  // Returns base ID (string) without "-backdrop"
    if (!dialogId) {
      log.warn('Modal element not found - cannot close dialog')
      return
    }

    try {
      const closeEvent = new CustomEvent('dialog:close', {
        detail: { dialogId: dialogId },
        bubbles: true
      })
      window.dispatchEvent(closeEvent)
      log.debug('Modal closed', { dialogId })
    } catch (error) {
      log.error('Failed to close modal', { error: error.message })
    }
  }

  toggleParent(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    const parentId = parseInt(event.currentTarget.dataset.parentId, 10)
    const childrenContainer = document.getElementById(`children-${parentId}`)
    const chevronIcon = event.currentTarget.querySelector('[data-chevron]')
    const parentButton = event.currentTarget

    if (!childrenContainer) {
      log.warn('Children container not found', { parentId })
      return
    }

    // Close previously opened parent
    if (this.currentOpenParentId && this.currentOpenParentId !== parentId) {
      const prevContainer = document.getElementById(`children-${this.currentOpenParentId}`)
      const prevButton = document.querySelector(`[data-parent-id="${this.currentOpenParentId}"]`)
      const prevChevron = prevButton?.querySelector('[data-chevron]')

      if (prevContainer) {
        prevContainer.classList.add('hidden')
        prevContainer.style.maxHeight = '0'
      }
      if (prevChevron) {
        prevChevron.style.transform = 'rotate(0deg)'
      }
      if (prevButton) {
        prevButton.classList.remove('bg-crust-50', 'border-b-2', 'border-crust-600')
      }
    }

    // Toggle current parent
    const isOpen = !childrenContainer.classList.contains('hidden')

    if (isOpen) {
      // Close
      childrenContainer.classList.add('hidden')
      childrenContainer.style.maxHeight = '0'
      if (chevronIcon) chevronIcon.style.transform = 'rotate(0deg)'
      parentButton.classList.remove('bg-crust-50', 'border-b-2', 'border-crust-600')
      this.currentOpenParentId = null
    } else {
      // Open
      childrenContainer.classList.remove('hidden')
      childrenContainer.style.maxHeight = `${childrenContainer.scrollHeight}px`
      if (chevronIcon) chevronIcon.style.transform = 'rotate(90deg)'
      parentButton.classList.add('bg-crust-50', 'border-b-2', 'border-crust-600')
      this.currentOpenParentId = parentId
    }

    log.debug('Parent toggled', { parentId, isOpen: !isOpen })
  }

  selectCategory(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    const categoryId = parseInt(event.currentTarget.dataset.categoryId, 10)
    const categoryName = event.currentTarget.dataset.categoryName
    // Handle parentName - it might be "null" string, null, or undefined
    const rawParentName = event.currentTarget.dataset.parentName
    const parentName = (rawParentName && rawParentName !== 'null') ? rawParentName : null

    // Update hidden input
    this.hiddenInputTarget.value = categoryId

    // Update display
    const breadcrumb = parentName ? `${parentName} > ${categoryName}` : categoryName
    this.selectedDisplayTarget.textContent = breadcrumb
    this.selectedDisplayTarget.classList.remove('text-charcoal-500')
    this.selectedDisplayTarget.classList.add('text-charcoal-700', 'font-medium')

    // Toggle medical compliance section based on category
    // For child categories, check the parent name; for parent categories, check the category name
    const checkName = parentName || categoryName
    log.debug('Checking medical compliance for category', {
      categoryName,
      parentName,
      rawParentName,
      checkName,
      willCheckAgainst: checkName
    })
    this.toggleMedicalCompliance(checkName)

    // Track selection for recent categories
    this.trackSelection(categoryId)

    // Close modal (use requestAnimationFrame to ensure DOM update completes)
    requestAnimationFrame(() => {
      this.close()
    })

    log.info('Category selected', { categoryId, categoryName, parentName })
  }

  // ========================================
  // KEYBOARD / VIEWPORT HANDLING
  // ========================================

  handleViewportResize() {
    // When the virtual keyboard opens, visualViewport.height shrinks.
    // Add bottom padding to keep content visible above the keyboard.
    const viewport = window.visualViewport
    if (!viewport) return

    const heightDelta = window.innerHeight - viewport.height
    const paddingBottom = Math.max(heightDelta, 0)
    const extraPadding = 16
    const totalPadding = `${paddingBottom + extraPadding}px`

    // Apply padding to all main view containers (Popular + All Categories sections)
    this.mainViewTargets.forEach(el => {
      el.style.paddingBottom = totalPadding
    })

    // Apply padding to search results (fixes keyboard covering search results on Android)
    if (this.hasSearchResultsTarget) {
      this.searchResultsTarget.style.paddingBottom = totalPadding
    }
    // Keep search bar sticky at top when keyboard opens
    if (this.hasSearchWrapperTarget) {
      this.searchWrapperTarget.style.position = 'sticky'
      this.searchWrapperTarget.style.top = '0'
      this.searchWrapperTarget.style.zIndex = '20'
      this.searchWrapperTarget.style.background = 'rgba(255,255,255,0.96)'
      this.searchWrapperTarget.style.backdropFilter = 'blur(6px)'
      this.searchWrapperTarget.style.paddingTop = '8px'
      this.searchWrapperTarget.style.paddingBottom = '8px'
    }

    // body scroll이 잠겨있으면 scrollIntoView 건너뛰기
    // 바텀시트 내부 scroll context에서 외부 scroll 조작으로 인한 충돌 방지
    // iOS Safari/WKWebView에서 스크롤 포커스 불안정 문제 해결
    if (scrollLocked()) {
      return
    }

    // Optional: ensure search input stays in view when keyboard opens
    // (only when body scroll is NOT locked)
    if (paddingBottom > 0 && this.hasSearchInputTarget) {
      this.searchInputTarget.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }
  }


  // ========================================
  // SEARCH FUNCTIONALITY
  // ========================================

  buildCategoriesCache() {
    const cache = []

    this.categoriesTree.forEach(parent => {
      // Hierarchical mode: index children
      if (parent.children && parent.children.length > 0) {
        parent.children.forEach(child => {
          cache.push({
            id: child.id,
            name: child.name,
            parentName: parent.name,
            searchText: `${parent.name} ${child.name}`.toLowerCase()
          })
        })
      } else {
        // Flat mode fallback: index parent itself
        cache.push({
          id: parent.id,
          name: parent.name,
          parentName: null,
          searchText: parent.name.toLowerCase()
        })
      }
    })

    log.debug('Categories cache built', { count: cache.length })
    return cache
  }

  search(event) {
    const query = event.target.value.trim().toLowerCase()

    if (query === '') {
      this.hideSearchResults()
      return
    }

    const results = this.categoriesCache.filter(cat =>
      cat.searchText.includes(query)
    )

    this.displaySearchResults(results)
    log.debug('Search executed', { query, resultsCount: results.length })
  }

  // Handle Enter/Go key: auto-select if single result, otherwise show results and dismiss keyboard
  searchOrSelect(event) {
    // Prevent default form submission and stop event bubbling to parent form (e.g., "下一步" button)
    event.preventDefault()
    event.stopPropagation()

    const query = event.target.value.trim().toLowerCase()

    if (query === '') {
      this.hideSearchResults()
      return
    }

    const results = this.categoriesCache.filter(cat =>
      cat.searchText.includes(query)
    )

    // Auto-select if exactly one result matches
    if (results.length === 1) {
      this.selectCategoryFromResult(results[0])
      return
    }

    // Multiple or no results: show results and dismiss keyboard
    this.displaySearchResults(results)
    event.target.blur()  // Dismiss virtual keyboard
    log.debug('Search submitted via Enter/Go', { query, resultsCount: results.length })
  }

  // Select category directly from search result object (used by searchOrSelect)
  selectCategoryFromResult(category) {
    // Update hidden input
    this.hiddenInputTarget.value = category.id

    // Update display with breadcrumb
    const breadcrumb = category.parentName
      ? `${category.parentName} > ${category.name}`
      : category.name
    this.selectedDisplayTarget.textContent = breadcrumb
    this.selectedDisplayTarget.classList.remove('text-charcoal-500')
    this.selectedDisplayTarget.classList.add('text-charcoal-700', 'font-medium')

    // Toggle medical compliance section based on category
    const checkName = category.parentName || category.name
    this.toggleMedicalCompliance(checkName)

    // Track selection for recent categories
    this.trackSelection(category.id)

    // Close modal
    requestAnimationFrame(() => {
      this.close()
    })

    log.info('Category selected from search', {
      categoryId: category.id,
      categoryName: category.name,
      parentName: category.parentName
    })
  }

  displaySearchResults(results) {
    // Hide main view, show search results
    this.mainViewTargets.forEach(el => el.classList.add('hidden'))
    this.searchResultsTarget.classList.remove('hidden')

    // Clear previous results
    this.searchResultsTarget.innerHTML = ''

    if (results.length === 0) {
      const noResultsText = getI18nMessage(this.element, 'noResults', '找不到相關分類')

      const emptyState = document.createElement('div')
      emptyState.className = 'text-center py-8 text-charcoal-500'
      emptyState.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 mx-auto mb-2 text-charcoal-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
          <circle cx="11" cy="11" r="8"/>
          <path d="m21 21-4.35-4.35"/>
        </svg>
        <p class="text-sm"></p>
      `
      emptyState.querySelector('p').textContent = noResultsText

      this.searchResultsTarget.innerHTML = ''
      this.searchResultsTarget.appendChild(emptyState)
      return
    }

    // Render results
    const resultsList = document.createElement('div')
    resultsList.className = 'space-y-1'

    results.forEach(cat => {
      const button = document.createElement('button')
      button.type = 'button'
      button.className = 'w-full px-4 py-3 text-left hover:bg-crust-50 transition-colors rounded-lg flex items-center justify-between'
      button.dataset.categoryId = cat.id
      button.dataset.categoryName = cat.name
      button.dataset.parentName = cat.parentName
      button.dataset.action = 'click->category-picker#selectCategory'

      // XSS Protection: Use textContent for user data
      const contentDiv = document.createElement('div')
      const nameDiv = document.createElement('div')
      nameDiv.className = 'text-sm font-medium text-charcoal-700'
      nameDiv.textContent = cat.name

      const parentDiv = document.createElement('div')
      parentDiv.className = 'text-xs text-charcoal-500'
      parentDiv.textContent = cat.parentName

      contentDiv.appendChild(nameDiv)
      contentDiv.appendChild(parentDiv)

      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
      svg.setAttribute('class', 'h-5 w-5 text-charcoal-400')
      svg.setAttribute('viewBox', '0 0 24 24')
      svg.setAttribute('fill', 'currentColor')
      svg.innerHTML = '<path d="M9.293 4.293a1 1 0 0 1 1.414 0l7 7a1 1 0 0 1 0 1.414l-7 7a1 1 0 0 1 -1.414 -1.414l6.293 -6.293l-6.293 -6.293a1 1 0 0 1 0 -1.414z"/>'

      button.appendChild(contentDiv)
      button.appendChild(svg)
      resultsList.appendChild(button)
    })

    this.searchResultsTarget.appendChild(resultsList)
  }

  hideSearchResults() {
    this.searchResultsTarget.classList.add('hidden')
    this.mainViewTargets.forEach(el => el.classList.remove('hidden'))
  }

  // ========================================
  // RENDER METHODS
  // ========================================

  renderAccordion() {
    // Safety check: ensure target is available before rendering
    if (!this.hasAccordionListTarget) {
      log.warn('accordionList target not available, deferring render')
      return
    }

    this.accordionListTarget.innerHTML = ''

    this.categoriesTree.forEach(parent => {
      const hasChildren = parent.children && parent.children.length > 0

      // Parent button wrapper
      const parentWrapper = document.createElement('div')
      parentWrapper.className = 'border-b border-charcoal-100 mb-0'

      // Parent button
      const parentButton = document.createElement('button')
      parentButton.type = 'button'
      parentButton.className = 'w-full px-4 py-4 bg-transparent active:bg-crust-50 transition-colors flex items-center justify-between'

      if (hasChildren) {
        // Hierarchical mode: parent toggles children
        parentButton.dataset.parentId = parent.id
        parentButton.dataset.action = 'click->category-picker#toggleParent'
      } else {
        // Flat mode: parent is directly selectable
        parentButton.dataset.categoryId = parent.id
        parentButton.dataset.categoryName = parent.name
        parentButton.dataset.parentName = null
        parentButton.dataset.action = 'click->category-picker#selectCategory'
      }

      // Parent name
      const parentName = document.createElement('span')
      parentName.className = 'text-sm font-medium text-charcoal-700'
      parentName.textContent = parent.name

      parentButton.appendChild(parentName)

      if (hasChildren) {
        // Chevron icon (only for hierarchical mode)
        const chevron = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
        chevron.setAttribute('class', 'h-5 w-5 text-charcoal-400 transition-transform duration-200')
        chevron.setAttribute('viewBox', '0 0 24 24')
        chevron.setAttribute('fill', 'currentColor')
        chevron.setAttribute('data-chevron', '')
        chevron.innerHTML = '<path d="M9.293 4.293a1 1 0 0 1 1.414 0l7 7a1 1 0 0 1 0 1.414l-7 7a1 1 0 0 1 -1.414 -1.414l6.293 -6.293l-6.293 -6.293a1 1 0 0 1 0 -1.414z"/>'
        parentButton.appendChild(chevron)

        // Children container (initially hidden)
        const childrenContainer = document.createElement('div')
        childrenContainer.id = `children-${parent.id}`
        childrenContainer.className = 'hidden bg-crumb-50 border-t border-charcoal-200 transition-all duration-300 overflow-hidden'
        childrenContainer.style.maxHeight = '0'

        // Sort children
        const sortedChildren = [...parent.children].sort((a, b) => {
          if (a.position === b.position) return a.name.localeCompare(b.name)
          return a.position - b.position
        })

        // Render children
        sortedChildren.forEach(child => {
          const childButton = document.createElement('button')
          childButton.type = 'button'
          childButton.className = 'w-full px-6 py-3 text-left active:bg-crust-100 transition-colors flex items-center justify-between border-b border-charcoal-100 last:border-0'
          childButton.dataset.categoryId = child.id
          childButton.dataset.categoryName = child.name
          childButton.dataset.parentName = parent.name
          childButton.dataset.action = 'click->category-picker#selectCategory'

          // Child name
          const childName = document.createElement('span')
          childName.className = 'text-sm text-charcoal-600'
          childName.textContent = child.name

          // Right arrow icon
          const arrow = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
          arrow.setAttribute('class', 'h-4 w-4 text-charcoal-300')
          arrow.setAttribute('viewBox', '0 0 24 24')
          arrow.setAttribute('fill', 'currentColor')
          arrow.innerHTML = '<path d="M9.293 4.293a1 1 0 0 1 1.414 0l7 7a1 1 0 0 1 0 1.414l-7 7a1 1 0 0 1 -1.414 -1.414l6.293 -6.293l-6.293 -6.293a1 1 0 0 1 0 -1.414z"/>'

          childButton.appendChild(childName)
          childButton.appendChild(arrow)
          childrenContainer.appendChild(childButton)
        })

        // Assemble accordion item with children
        parentWrapper.appendChild(parentButton)
        parentWrapper.appendChild(childrenContainer)
      } else {
        // Flat mode: just add parent button (no children container)
        parentWrapper.appendChild(parentButton)
      }

      this.accordionListTarget.appendChild(parentWrapper)
    })

    log.debug('Accordion rendered', { parentsCount: this.categoriesTree.length })
  }

  resetView() {
    // Close any opened parent
    if (this.currentOpenParentId) {
      const container = document.getElementById(`children-${this.currentOpenParentId}`)
      const button = document.querySelector(`[data-parent-id="${this.currentOpenParentId}"]`)
      const chevron = button?.querySelector('[data-chevron]')

      if (container) {
        container.classList.add('hidden')
        container.style.maxHeight = '0'
      }
      if (chevron) {
        chevron.style.transform = 'rotate(0deg)'
      }
    }

    this.currentOpenParentId = null
    if (this.hasSearchInputTarget) this.searchInputTarget.value = ''
    this.hideSearchResults()
  }

  // ========================================
  // MEDICAL COMPLIANCE TOGGLE
  // ========================================

  toggleMedicalCompliance(categoryName) {
    if (!this.hasMedicalComplianceSectionTarget) {
      log.debug('Medical compliance section target not found')
      return
    }

    if (!this.hasMedicalCategoriesValue || this.medicalCategoriesValue.length === 0) {
      log.debug('Medical categories not configured')
      return
    }

    // Debug logging
    log.debug('toggleMedicalCompliance called', {
      categoryName,
      medicalCategories: this.medicalCategoriesValue,
      hasSectionTarget: this.hasMedicalComplianceSectionTarget
    })

    // Check if category requires medical compliance
    const requiresCompliance = this.medicalCategoriesValue.includes(categoryName)

    log.debug('Medical compliance check result', {
      categoryName,
      requiresCompliance,
      includes: this.medicalCategoriesValue.includes(categoryName)
    })

    if (requiresCompliance) {
      // Show medical compliance fields
      this.medicalComplianceSectionTarget.classList.remove('hidden')

      // Mark fields as required
      const permitField = this.medicalComplianceSectionTarget.querySelector('[name*="ad_permit_number"]')
      const licenseField = this.medicalComplianceSectionTarget.querySelector('[name*="medical_license"]')

      if (permitField) permitField.required = true
      if (licenseField) licenseField.required = true

      log.info('Medical compliance fields shown', { categoryName })
    } else {
      // Hide medical compliance fields
      this.medicalComplianceSectionTarget.classList.add('hidden')

      // Remove required attribute and clear values
      const permitField = this.medicalComplianceSectionTarget.querySelector('[name*="ad_permit_number"]')
      const licenseField = this.medicalComplianceSectionTarget.querySelector('[name*="medical_license"]')

      if (permitField) {
        permitField.required = false
        permitField.value = ''
      }
      if (licenseField) {
        licenseField.required = false
        licenseField.value = ''
      }

      log.info('Medical compliance fields hidden', { categoryName })
    }
  }

  // ========================================
  // RECENT CATEGORIES TRACKING
  // ========================================

  async trackSelection(categoryId) {
    if (!this.hasUserIdValue || !this.userIdValue) {
      log.debug('Skipping tracking - no user ID')
      return
    }

    try {
      const response = await fetch('/api/user_category_selections', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content || ''
        },
        body: JSON.stringify({ category_id: categoryId })
      })

      if (response.ok) {
        log.debug('Category selection tracked', { categoryId })
      } else {
        log.warn('Failed to track selection', { status: response.status })
      }
    } catch (error) {
      log.error('Category tracking failed', { error: error.message })
    }
  }

  // ========================================
  // MEDICAL COMPLIANCE INITIALIZATION
  // ========================================

  /**
   * Initialize medical compliance section visibility on page load
   * Fixes bug where ERB renders initial state based on persisted category,
   * but JavaScript only updates visibility on user click events
   *
   * Scenario: User selects medical category → navigates away → returns to step 2
   * Result: ERB renders class="" (visible), but user hasn't clicked yet
   * Fix: Check current category on connect() and sync visibility
   */
  initializeMedicalCompliance() {
    if (!this.hasMedicalComplianceSectionTarget) {
      log.debug('Medical compliance section target not found, skipping initialization')
      return
    }

    // Check if there's a currently selected category
    if (!this.hasHiddenInputTarget || !this.hiddenInputTarget.value) {
      // No category selected → default to hidden
      this.medicalComplianceSectionTarget.classList.add('hidden')
      log.debug('No category selected, hiding medical compliance section')
      return
    }

    // Find the selected category in cache
    const selectedCategoryId = parseInt(this.hiddenInputTarget.value, 10)
    const category = this.findCategoryById(selectedCategoryId)

    if (!category) {
      // Category not found in cache → default to hidden
      this.medicalComplianceSectionTarget.classList.add('hidden')
      log.warn('Selected category not found in cache', { categoryId: selectedCategoryId })
      return
    }

    // Use same logic as selectCategory(): check parent name for children
    const checkName = category.parentName || category.name
    this.toggleMedicalCompliance(checkName)

    log.info('Medical compliance section initialized', {
      categoryId: selectedCategoryId,
      categoryName: category.name,
      parentName: category.parentName,
      checkName,
      requiresCompliance: this.medicalCategoriesValue.includes(checkName)
    })
  }

  /**
   * Find category by ID in the categories cache
   * @param {number} id - Category ID to find
   * @returns {object|undefined} Category object with id, name, parentId, parentName
   */
  findCategoryById(id) {
    return this.categoriesCache.find(cat => cat.id === id)
  }
}
